import { useCompositionsList, useCurrentComposition } from "@design-lab/entities/composition";
import { useArtworkForm } from "@design-lab/entities/artwork";
import { useDesignLabPrices } from "@design-lab/entities/composition/model";
import { useEditMode } from "@design-lab/shared/hooks/useEditMode";
import { PLACEMENTS } from "@design-lab/shared/constants";
import type { CartProduct } from "@design-lab/shared/types";

const DEFAULT_ITEM_PRICE = 10.0;

export function useCartProducts() {
  const { form: artworkForm } = useArtworkForm();
  const { formCompositions, products } = useCompositionsList();
  const { currentProduct, currentComposition } = useCurrentComposition();
  const { isEditMode, assemblyGroupId } = useEditMode();

  const activePlacements = PLACEMENTS.filter(placement => {
    const placementData = artworkForm.getValues(`${placement}`);
    return placementData && placementData.artworkId && placementData.size;
  });

  const placementsForPricing = activePlacements.length > 0 ? activePlacements : ["front"];
  const { data: prices } = useDesignLabPrices(
    placementsForPricing,
    isEditMode ? assemblyGroupId : undefined
  );

  const shirtCount =
    formCompositions?.reduce(
      (total, composition) =>
        total +
        Object.values(composition.variants).reduce(
          (sum: number, quantity) => sum + (typeof quantity === "number" ? quantity : 0),
          0
        ),
      0
    ) || 0;

  const createProductsArray = (): CartProduct[] => {
    const artworkData = artworkForm.getValues();
    const cartProducts: CartProduct[] = [];
    const totalCartPrice = prices?.effectivePrice ?? DEFAULT_ITEM_PRICE * shirtCount;

    formCompositions?.forEach(formComposition => {
      const productQuery = products.find(p => p.data?.catalogNumber === formComposition.catalogId);
      const compositionProduct = productQuery?.data;

      if (!compositionProduct) return;

      const productComposition = compositionProduct.compositions.find(
        c => c.id === formComposition.id
      );

      if (!productComposition?.variants || Object.keys(formComposition.variants).length === 0) {
        return;
      }

      const sizes: Array<{ size: string; quantity: number }> = [];
      let totalQuantity = 0;

      Object.entries(formComposition.variants).forEach(([variantId, quantity]) => {
        const quantityNum = typeof quantity === "number" ? quantity : 0;
        if (quantityNum > 0) {
          const variant = productComposition.variants.find(v => v.id === variantId);
          if (variant) {
            sizes.push({ size: variant.size, quantity: quantityNum });
            totalQuantity += quantityNum;
          }
        }
      });

      if (totalQuantity > 0) {
        const frontArtwork = artworkData.front || artworkData.frontLeftChest;
        const designFront =
          frontArtwork?.size?.width && frontArtwork?.size?.height && frontArtwork?.artworkId
            ? `${frontArtwork.size.width.toFixed(2)}" x ${frontArtwork.size.height.toFixed(2)}"`
            : undefined;

        const backArtwork = artworkData.back;
        const designBack =
          backArtwork?.size?.width && backArtwork?.size?.height && backArtwork?.artworkId
            ? `${backArtwork.size.width.toFixed(2)}" x ${backArtwork.size.height.toFixed(2)}"`
            : undefined;

        const proportionalPrice =
          shirtCount > 0
            ? (totalCartPrice * totalQuantity) / shirtCount
            : DEFAULT_ITEM_PRICE * totalQuantity;

        const product: CartProduct = {
          productName: compositionProduct.name || "Custom Design",
          color: formComposition.colorName,
          catalogNumber: compositionProduct.catalogNumber,
          designFront,
          designBack,
          sizes,
          totalQuantity,
          totalPrice: proportionalPrice,
          brandImageUrl: compositionProduct.brandLogo
        };

        cartProducts.push(product);
      }
    });

    if (cartProducts.length === 0) {
      const fallbackProduct: CartProduct = {
        productName: currentProduct?.name || "Custom Design",
        color: currentComposition?.colorName || "Selected Color",
        designFront: undefined,
        designBack: undefined,
        catalogNumber: currentProduct?.catalogNumber,
        sizes: [{ size: "M", quantity: 1 }],
        totalQuantity: 1,
        totalPrice: totalCartPrice || DEFAULT_ITEM_PRICE,
        brandImageUrl: currentProduct?.brandLogo || undefined
      };

      cartProducts.push(fallbackProduct);
    }

    return cartProducts;
  };

  const calculateCartTotal = (): number =>
    prices?.effectivePrice ?? DEFAULT_ITEM_PRICE * shirtCount;

  return {
    shirtCount,
    createProductsArray,
    calculateCartTotal
  };
}
